import type { Conversation } from '.';
import type { PetSpecies, PetState } from '../buruburu/pet';
import type {
  DescriptiveScript,
  Dialog,
  DialogScenes,
  Script,
} from './conservation.ruleset';

export const conversationGetters = (
  Conversation: Conversation,
  { VisitRecord }: Conversation['$DEPENDENCIES'],
) => {
  return (state: Conversation.State) => ({
    getTimesSeen: (dialogId: Dialog['id']) => getTimesSeen(state, dialogId),

    hasOpenedDialog: () => Object.keys(state.conversation.dialogs).length > 0,

    getValidDialogs: (
      scene: DialogScenes,
      species: PetSpecies,
      petState: PetState,
      answers: Record<string, typeof state.conversation.answers[string]>,
    ) => {
      let minTimesSeen: number;
      const dialogs = Object.values(Conversation.ruleset.dialogs).filter(
        (dialog) => {
          if (!dialog.scenes.some((x) => x === scene)) {
            return false;
          }

          if (dialog.pet_id !== species) {
            return false;
          }

          if (dialog.pet_states && !dialog.pet_states.includes(petState)) {
            return false;
          }

          if (dialog.req_answers?.some((answerKey) => !answers[answerKey])) {
            return false;
          }

          const timesSeen = getTimesSeen(state, dialog.id);
          if (minTimesSeen === undefined || timesSeen < minTimesSeen) {
            minTimesSeen = timesSeen;
          }

          return true;
        },
      );

      return dialogs.filter(
        ({ id }) => getTimesSeen(state, id) === minTimesSeen ?? 0,
      );
    },

    getScriptQuestion: (
      scriptId: DescriptiveScript['id'],
      locale: string,
      isFriend: boolean,
    ) =>
      substituteScriptTokens(
        Conversation.ruleset.scripts[scriptId][
          `question_${getLocaleSuffix(locale)}`
        ],
        state,
        isFriend,
      ),

    getScriptText: (
      scriptId: Script['id'],
      locale: string,
      isFriend: boolean,
    ) =>
      substituteScriptTokens(
        Conversation.ruleset.scripts[scriptId][
          `text_${getLocaleSuffix(locale)}`
        ],
        state,
        isFriend,
      ),
  });

  function substituteScriptTokens(
    script: string,
    state: Conversation.State,
    isFriend: boolean,
  ) {
    // TODO Lookup substitutions based on tokens found in text
    const substitutions = {
      // Player's pet's name
      // Will be replaced with friend's pet's name when chatting with a friend
      pet_name:
        (isFriend && state.visit.visitSession?.petName) || state.pet.name,

      // Player's profile name
      player_name: state.publicProfile?.name ?? state.profile.name,

      // Answers to any conversation questions given by the player
      ...Object.entries(state.conversation.answers).reduce(
        (acc, [answerVar, answer]) => {
          acc[answerVar] = answer.value;
          return acc;
        },
        {},
      ),

      // Friend's display name
      // This refers to the player currently being visited (if any), otherwise
      // it refers to our most recent visitor (so our pet can tell us who visited)
      'friend.player_name':
        state.visit.visitSession?.name ??
        VisitRecord.getters.getMostRecentVisitor(state)?.name,

      // Duo
      'inviter.player_name': state.duo.lastReceivedInvitation.name,
      'invitee.player_name':
        state.duo.lastResponse?.name ?? state.visit.visitSession?.name,
      'invitee.message':
        state.duo.invitations.sent.find(
          ({ timestamp, playerId }) =>
            state.duo.lastResponse?.timestamp === timestamp &&
            state.duo.lastResponse?.playerId === playerId,
        )?.message ??
        state.duo.invitations.received.find(
          ({ timestamp, playerId }) =>
            state.duo.lastResponse?.timestamp === timestamp &&
            state.duo.lastResponse?.playerId === playerId,
        )?.message,

      // Answers to any conversation questions given by the friend
      ...Object.entries(state.visit.visitSession?.answers ?? {}).reduce(
        (acc, [answerVar, answer]) => {
          acc[`friend.${answerVar}`] = answer.value;
          return acc;
        },
        {},
      ),
    };
    for (const token in substitutions) {
      script = script.replace('{' + token + '}', substitutions[token]);
    }

    return script;
  }
};

const getTimesSeen = (
  { conversation }: Conversation.State,
  dialog: Dialog['id'],
) => conversation.dialogs[dialog]?.timesSeen ?? 0;

const getLocaleSuffix = (locale: string) => (locale === 'en' ? 'en' : 'jp');
